// src/services/linkedinService.js
// Serviço de integração com LinkedIn API

const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
const LINKEDIN_CLIENT_SECRET = process.env.REACT_APP_LINKEDIN_CLIENT_SECRET;
const LINKEDIN_REDIRECT_URI = process.env.REACT_APP_LINKEDIN_REDIRECT_URI || `${window.location.origin}/admin/linkedin/callback`;

// Escopos necessários para publicar posts
const LINKEDIN_SCOPES = ['openid', 'profile', 'w_member_social'];

// ==========================================
// AUTENTICAÇÃO OAUTH 2.0
// ==========================================

// Gera URL de autorização do LinkedIn
export const getAuthorizationUrl = () => {
  const state = generateRandomState();
  sessionStorage.setItem('linkedin_oauth_state', state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: LINKEDIN_CLIENT_ID,
    redirect_uri: LINKEDIN_REDIRECT_URI,
    state: state,
    scope: LINKEDIN_SCOPES.join(' ')
  });

  return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
};

// Gera string aleatória para state (segurança OAuth)
const generateRandomState = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Troca authorization code por access token
export const exchangeCodeForToken = async (code, state) => {
  // Verifica state para prevenir CSRF
  const savedState = sessionStorage.getItem('linkedin_oauth_state');
  if (state !== savedState) {
    throw new Error('Estado OAuth inválido. Possível ataque CSRF.');
  }
  sessionStorage.removeItem('linkedin_oauth_state');

  try {
    // Usa função serverless do Netlify para evitar problemas de CORS
    const response = await fetch('/.netlify/functions/linkedin-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code: code,
        redirect_uri: LINKEDIN_REDIRECT_URI
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Erro ao obter token');
    }

    // Salva token no localStorage
    saveLinkedInToken(data);

    return data;
  } catch (error) {
    console.error('Erro ao trocar code por token:', error);
    throw error;
  }
};

// Salva token do LinkedIn
const saveLinkedInToken = (tokenData) => {
  const expiresAt = Date.now() + (tokenData.expires_in * 1000);
  localStorage.setItem('linkedin_access_token', tokenData.access_token);
  localStorage.setItem('linkedin_token_expires_at', expiresAt.toString());
  if (tokenData.refresh_token) {
    localStorage.setItem('linkedin_refresh_token', tokenData.refresh_token);
  }
};

// Obtém token salvo (se válido)
export const getStoredToken = () => {
  const token = localStorage.getItem('linkedin_access_token');
  const expiresAt = localStorage.getItem('linkedin_token_expires_at');

  if (!token || !expiresAt) {
    return null;
  }

  // Verifica se expirou
  if (Date.now() > parseInt(expiresAt)) {
    clearLinkedInToken();
    return null;
  }

  return token;
};

// Limpa tokens salvos
export const clearLinkedInToken = () => {
  localStorage.removeItem('linkedin_access_token');
  localStorage.removeItem('linkedin_token_expires_at');
  localStorage.removeItem('linkedin_refresh_token');
  localStorage.removeItem('linkedin_user_id');
};

// Verifica se está conectado ao LinkedIn
export const isLinkedInConnected = () => {
  return getStoredToken() !== null;
};

// ==========================================
// PERFIL DO USUÁRIO
// ==========================================

// Obtém informações do perfil (via Netlify function para evitar CORS)
export const getProfile = async () => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('Não conectado ao LinkedIn');
  }

  try {
    const response = await fetch('/.netlify/functions/linkedin-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'profile',
        accessToken: token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        clearLinkedInToken();
        throw new Error('Token expirado. Reconecte ao LinkedIn.');
      }
      throw new Error(data.error || 'Erro ao obter perfil');
    }

    // Salva o ID do usuário para usar nas publicações
    localStorage.setItem('linkedin_user_id', data.sub);

    return data;
  } catch (error) {
    console.error('Erro ao obter perfil:', error);
    throw error;
  }
};

// ==========================================
// PUBLICAÇÃO DE POSTS
// ==========================================

// Publica um post de texto no LinkedIn (via Netlify function para evitar CORS)
export const publishPost = async (text, articleUrl = null) => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('Não conectado ao LinkedIn');
  }

  let userId = localStorage.getItem('linkedin_user_id');
  if (!userId) {
    // Tenta obter o perfil para pegar o ID
    const profile = await getProfile();
    userId = profile.sub;
  }

  try {
    // Usa função Netlify para evitar CORS
    const response = await fetch('/.netlify/functions/linkedin-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'publish',
        userId: userId,
        accessToken: token,
        text: text,
        articleUrl: articleUrl
      })
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        clearLinkedInToken();
        throw new Error('Token expirado. Reconecte ao LinkedIn.');
      }
      throw new Error(data.error || 'Erro ao publicar no LinkedIn');
    }

    return data;
  } catch (error) {
    console.error('Erro ao publicar:', error);
    throw error;
  }
};

// ==========================================
// UPLOAD DE IMAGEM
// ==========================================

// Registra upload de imagem no LinkedIn (Passo 1)
export const registerImageUpload = async () => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('Não conectado ao LinkedIn');
  }

  let userId = localStorage.getItem('linkedin_user_id');
  if (!userId) {
    const profile = await getProfile();
    userId = profile.sub;
  }

  // Usa função Netlify para evitar CORS
  const response = await fetch('/.netlify/functions/linkedin-image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      action: 'register',
      userId: userId,
      accessToken: token
    })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro ao registrar upload');
  }

  return data;
};

// Faz upload da imagem (Passo 2)
export const uploadImage = async (uploadUrl, imageFile) => {
  // Usa função Netlify para evitar CORS
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('uploadUrl', uploadUrl);
  formData.append('accessToken', getStoredToken());

  const response = await fetch('/.netlify/functions/linkedin-image', {
    method: 'PUT',
    body: formData
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Erro ao fazer upload da imagem');
  }

  return { success: true };
};

// Publica post com imagem (via Netlify function para evitar CORS)
export const publishPostWithImage = async (text, imageFile) => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('Não conectado ao LinkedIn');
  }

  let userId = localStorage.getItem('linkedin_user_id');
  if (!userId) {
    const profile = await getProfile();
    userId = profile.sub;
  }

  // Passo 1: Registrar upload
  const registerData = await registerImageUpload();
  const uploadUrl = registerData.uploadUrl;
  const asset = registerData.asset;

  // Passo 2: Fazer upload da imagem
  await uploadImage(uploadUrl, imageFile);

  // Passo 3: Publicar post com a imagem (via Netlify function)
  try {
    const response = await fetch('/.netlify/functions/linkedin-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'publish',
        userId: userId,
        accessToken: token,
        text: text,
        asset: asset
      })
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        clearLinkedInToken();
        throw new Error('Token expirado. Reconecte ao LinkedIn.');
      }
      throw new Error(data.error || 'Erro ao publicar no LinkedIn');
    }

    return data;
  } catch (error) {
    console.error('Erro ao publicar:', error);
    throw error;
  }
};

// ==========================================
// UTILITÁRIOS
// ==========================================

// Verifica se as credenciais do LinkedIn estão configuradas
export const isLinkedInConfigured = () => {
  return Boolean(LINKEDIN_CLIENT_ID);
};

// Abre popup de autorização
export const openAuthPopup = () => {
  const url = getAuthorizationUrl();
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    url,
    'linkedin_auth',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
  );

  return popup;
};

// Processa callback do OAuth (chamado pela página de callback)
export const handleOAuthCallback = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) {
    throw new Error(params.get('error_description') || 'Autorização negada');
  }

  if (!code) {
    throw new Error('Código de autorização não encontrado');
  }

  return exchangeCodeForToken(code, state);
};

export default {
  getAuthorizationUrl,
  exchangeCodeForToken,
  getStoredToken,
  clearLinkedInToken,
  isLinkedInConnected,
  isLinkedInConfigured,
  getProfile,
  publishPost,
  openAuthPopup,
  handleOAuthCallback
};
